import styled, { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { getBrandColorUsingCssFilterOnImg } from '@/utils/cssColorFilters.ts';
import { Text } from '@lmn/design-system';
import { checkboxHoverColor } from '../../../../styles/colors';
import { loadAnimation, imageLoadingAnimationPrimary, userSelectNone } from '../../../../styles/mixins';
import type { Localization } from '../../../../common/Localization';
import { getLoaderSizeFromLabel } from './utils';

const iconLeftStyles = css`
  padding-left: 16px;
  picture {
    margin-right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const iconRightStyles = css`
  padding-right: 8px;

  picture {
    margin-left: 8px;
  }
`;

const toastStyles = css`
  border-radius: 24px;
  border: none;

  picture {
    margin-left: 0;
    width: 16px;
    height: 16px;
  }
`;

const darkstyles = css`
  background-color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  border: none;

  &::after {
    background-color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  }

  picture {
    filter: invert(1) brightness(10);
  }

  &:after {
    background-color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  }

  &:hover {
    color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
    img {
      filter: unset;
    }
  }
`;

const darkWaitingStyles = css`
  opacity: 0.2;
`;

const toastAndDarkStyles = css`
  box-shadow: unset;
`;

const dividerStyles = css`
  &:after {
    content: '';
    position: absolute;
    width: 1px;
    height: 24px;
    right: 0;
    background-color: ${({ theme }) => theme.kallisto.color.common.black[100]};
    margin: 0;
  }

  &:last-child:after {
    content: unset;
  }
`;

const allFiltersStyles = css<{ hideDivider?: boolean }>`
  ${({ hideDivider }) => !hideDivider && dividerStyles};

  &:after {
    right: -10px;
  }
`;

const selectedStyled = ({ selected }: { selected?: boolean }) => {
  if (selected) {
    return css`
      svg {
        margin-left: 8px;

        circle {
          // TODO
          // fill: primary;
        }

        line {
          stroke: white;
        }
      }
    `;
  }

  return css``;
};

const selectedDesktopStyled = ({ selected, localization }: { selected?: boolean; localization: Localization }) =>
  selected &&
  css`
    svg {
      margin-left: 8px;

      circle {
        fill: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      }
      line {
        stroke: white;
      }
    }

    @media (min-width: 1024px) {
      background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
      border: 1px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
      box-shadow: 0 0 0 1.5px ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      ${getBrandColorUsingCssFilterOnImg(localization.brand)}
    }
  `;

const hoverStyles = css<{ localization: Localization }>`
  cursor: pointer;
  &:hover {
    border: 1px solid transparent;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    ${({ localization }) => getBrandColorUsingCssFilterOnImg(localization.brand)};

    svg {
      margin-left: 8px;

      circle {
        fill: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      }

      line {
        stroke: white;
      }
    }
  }
`;

const openStyles = ({
  open,
  hasIconLeft,
  localization,
}: {
  open: boolean;
  hasIconLeft: boolean;
  localization: Localization;
}) =>
  open &&
  !hasIconLeft &&
  css`
    border: 1px solid transparent;
    box-shadow: 0 0 0 1.5px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};

    picture {
      transform: rotate(180deg);
    }

    ${getBrandColorUsingCssFilterOnImg(localization.brand)}
  `;

const loadingStyles = css`
  padding-right: 16px;
`;

const desktopStyles = css<{ localization: Localization }>`
  transition: box-shadow 0.2s ease-out;
`;

export const PillContainer = styled.button<{
  open: boolean;
  size: number;
  selected?: boolean;
  isAllFilters: boolean;
  localization: Localization;
  showMapButtonMobile: boolean;
  isWaiting: boolean;
  hasIconLeft: boolean;
  hasIconRight: boolean;
  isToast?: boolean;
  isDark?: boolean;
  isDesktop: boolean;
  hideDivider?: boolean;
}>`
  height: 40px;
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 19px;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  position: relative;
  white-space: nowrap;
  scroll-snap-align: center;

  picture {
    width: 20px;
    height: 20px;
    transition: transform 0.2s;
    transition-duration: 0.2s, 0.2s;
    transition-timing-function: ease-in, ease-out;
  }

  @media (max-width: 1023px) {
    box-shadow: unset;
    background-color: unset;
    width: 33.3%;
    justify-content: center;
  }

  @media (min-width: 1024px) {
    margin-right: 10px;
  }

  ${userSelectNone};
  ${({ hasIconLeft }) => hasIconLeft && iconLeftStyles};
  ${({ hasIconRight }) => hasIconRight && iconRightStyles};
  ${({ isDesktop }) => !isDesktop && dividerStyles};
  ${({ isDesktop }) => isDesktop && desktopStyles};
  ${({ isAllFilters, isDesktop }) => isAllFilters && isDesktop && allFiltersStyles};
  ${({ isToast }) => isToast && toastStyles};
  ${({ isDark }) => isDark && darkstyles};
  ${({ isToast, isDark }) => isToast && isDark && toastAndDarkStyles};
  ${({ isDark, isWaiting }) => isDark && isWaiting && darkWaitingStyles};
  ${({ selected }) => selectedStyled({ selected })};
  ${({ selected, localization }) => selectedDesktopStyled({ selected, localization })};
  ${({ open, hasIconLeft, localization }) => openStyles({ open, hasIconLeft, localization })};
  ${({ isWaiting, isDesktop }) => isWaiting && isDesktop && loadingStyles};
  ${({ isWaiting, isDesktop }) => isDesktop && !isWaiting && hoverStyles};
`;

export const LoadingBar = styled.div<{ label: string; isSelected?: boolean }>`
  width: 100px;
  height: 16px;
  border-radius: 8px;
  position: relative;
  ${({ isSelected }) => (isSelected ? imageLoadingAnimationPrimary : loadAnimation)}
  ${({ label }) =>
    label &&
    css`
      width: ${getLoaderSizeFromLabel(label)};
    `};
`;

export const sortOption = css<{ localization: Localization }>`
  width: 100%;

  > div {
    padding: 0 16px;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:nth-child(3) {
      border-top: 1px solid ${({ theme }) => theme.kallisto.color.common.black[50]};
      border-bottom: 1px solid ${({ theme }) => theme.kallisto.color.common.black[50]};
      @media only screen and (min-width: 768px) {
        border: none;
      }
    }

    @media only screen and (min-width: 768px) {
      height: 48px;
      line-height: 48px;
    }

    &:hover {
      background-color: ${checkboxHoverColor};
    }

    &.selected-radio {
      ${({ localization }) => getBrandColorUsingCssFilterOnImg(localization.brand)}
    }

    label {
      width: 100%;

      svg {
        display: none;
      }
    }

    svg {
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    }
  }
`;

export const mobileSortOption = css<{ brand?: Brand }>`
  width: 100%;
  margin-bottom: 0;

  > div {
    padding: 0 16px;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    height: 56px;
    line-height: 56px;
    border-bottom: 1px solid ${({ theme }) => theme.kallisto.color.common.black[50]};
    margin-bottom: 0;

    &:last-child {
      border: none;
    }

    @media only screen and (min-width: 768px) {
      min-height: 40px;
      line-height: 40px;
    }

    &:hover,
    &.selected-radio {
      ${({ brand }) => getBrandColorUsingCssFilterOnImg(brand)}
    }

    label {
      width: 100%;

      svg {
        display: none;
      }
    }

    svg {
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    }
  }
`;

export const sortOverlayStyles = css`
  padding: 0;
  overflow: hidden;
  border-radius: 9px;

  .oss-ui-form {
    width: calc(100% - 32px);
    padding: 0;

    @media only screen and (min-width: 768px) {
      width: 100%;
    }

    .oss-ui-radio {
      margin-bottom: 0;

      > div {
        margin-bottom: 0;
      }
    }
  }
`;

export const LabelText = styled(Text)``;
